import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import StatusChip from '../../../components/StatusChip';
import EditIcon from '@mui/icons-material/Edit';
import { currencyFormatter } from '../../../utils/currencyFormat';
import CustomPagination from '../../../components/customPagination';
import { formatDate } from '../../../utils/formatDate';
import { useGetSalesCreditNoteRegistersHandler } from '../dataHandlers/getSalesCreditNoteRegistersHandler';
import { useNavigate } from 'react-router-dom';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { UPDATE_SALE_REGISTER } from '../../../constants/eInvoicePermissions';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';

const getDocumentId = (row) => row.uuid || row.id;

const SalesCreditNoteRegisterTable = ({ handlePaginationChange, searchQuery = {} }) => {
  const navigate = useNavigate();
  const { salesInvoiceRegister } = useTranslatedMessages();
  const t = salesInvoiceRegister?.table ?? {};
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const canUpdate = hasEInvoicePermission(UPDATE_SALE_REGISTER);
  const { registers, totalDocuments, isLoading } = useGetSalesCreditNoteRegistersHandler({ searchQuery });

  const navigateToRow = (row) => {
    const documentId = getDocumentId(row);
    if (row.document_mode === 'E_INVOICE') {
      navigate(`/sales/documents/credit-notes/${documentId}`);
    } else {
      navigate(`/sales-invoice-register/credit-notes/${documentId}`);
    }
  };

  const columns = [
    {
      headerName: t.source ?? 'Source',
      field: 'document_mode',
      flex: 0.8,
      renderCell: (params) => {
        const isEInvoice = params.row.document_mode === 'E_INVOICE';
        return (
          <StatusChip
            label={
              isEInvoice
                ? (t.source_e_invoice ?? 'E-Invoice')
                : (t.source_non_e_invoice ?? t.source_register ?? 'Non-E-Invoice')
            }
            size="small"
            color={isEInvoice ? 'success' : 'default'}
            icon={'none'}
          />
        );
      },
    },
    {
      headerName: t.credit_note_no ?? 'Credit Note No',
      field: 'document_number',
      flex: 1,
      renderCell: (params) => (
        <Typography
          component="span"
          variant="span"
          onClick={() => navigateToRow(params.row)}
          sx={{
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline', color: 'primary.main' },
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      headerName: t.customer_info ?? 'Customer',
      field: 'accounting_customer_party',
      flex: 1,
      valueGetter: (params) => params?.name ?? t.not_available ?? 'N/A',
    },
    {
      headerName: t.credit_note_date ?? 'Credit Note Date',
      field: 'issue_date',
      flex: 1,
      valueGetter: (params) => formatDate(params),
    },
    {
      headerName: t.amount ?? 'Amount',
      field: 'payable_amount',
      flex: 1,
      renderCell: ({ row }) =>
        row.document_currency_code === 'AED'
          ? `AED ${currencyFormatter().format(row.payable_amount)}`
          : currencyFormatter(row.document_currency_code).format(row.payable_amount),
    },
    {
      field: 'actions',
      headerName: t.actions ?? 'Actions',
      flex: 0.6,
      sortable: false,
      renderCell: (params) => {
        if (params.row.document_mode !== 'NON_E_INVOICE') {
          return null;
        }
        return (
          <PermissionDeniedWrapper show={!canUpdate} title={accessDeniedMessage}>
            <Tooltip title={t.edit ?? 'Edit'}>
              <IconButton
                size="small"
                disabled={!canUpdate}
                onClick={() => navigate(`/sales-invoice-register/credit-notes/${getDocumentId(params.row)}/edit`)}
                data-testid="edit-credit-note-register-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </PermissionDeniedWrapper>
        );
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={registers}
        columns={columns}
        getRowId={(row) => row.id || row.uuid}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />
    </Box>
  );
};

export default SalesCreditNoteRegisterTable;
